import { useCallback } from 'react'

import type { Invoice, InvoiceStatus } from '../types/invoice'
import { useRestoreInvoiceMutation } from './useRestoreInvoiceMutation'
import { useUpdateInvoiceMutation } from './useUpdateInvoiceMutation'
import { useVoidInvoiceMutation } from './useVoidInvoiceMutation'

interface Options {
  // Called when the user picks "Partially Paid" in the status select; the
  // partial flow is local-only until the paid amount is actually edited, so
  // the hook delegates the UI side-effects (expand row, focus paid input,
  // remember the pending invoice) to the caller.
  onPartialSelected?: (invoice: Invoice) => void
}

export function useInvoiceStatusActions(dealId: string, options: Options = {}) {
  const updateMutation = useUpdateInvoiceMutation()
  const voidMutation = useVoidInvoiceMutation()
  const restoreMutation = useRestoreInvoiceMutation()

  const { onPartialSelected } = options

  const applyStatusChange = useCallback(
    (invoice: Invoice, value: InvoiceStatus): void => {
      if (value === 'Paid') {
        const totalCents = invoice.totalCents ?? 0
        const paidDate = invoice.paidDate ?? new Date().toISOString().slice(0, 10)
        updateMutation.mutate({
          dealId,
          invoiceId: invoice.id,
          patch: {
            status: 'Paid',
            paidAmountCents: totalCents,
            outstandingCents: 0,
            paidDate,
          },
          body: {
            status: 'Paid',
            paidAmountCents: totalCents,
            paidDate,
          },
        })
        return
      }
      if (value === 'Unpaid') {
        const totalCents = invoice.totalCents ?? 0
        updateMutation.mutate({
          dealId,
          invoiceId: invoice.id,
          patch: {
            status: 'Unpaid',
            paidAmountCents: 0,
            outstandingCents: totalCents,
            paidInfo: undefined,
          },
          body: {
            status: 'Unpaid',
            paidAmountCents: 0,
            paidInfo: undefined,
          },
        })
        return
      }
      onPartialSelected?.(invoice)
    },
    [dealId, updateMutation, onPartialSelected],
  )

  const voidInvoice = useCallback(
    (invoice: Invoice): void => {
      voidMutation.mutate({ dealId, invoiceId: invoice.id })
    },
    [dealId, voidMutation],
  )

  const restoreInvoice = useCallback(
    (invoice: Invoice): void => {
      restoreMutation.mutate({ dealId, invoiceId: invoice.id })
    },
    [dealId, restoreMutation],
  )

  return { applyStatusChange, voidInvoice, restoreInvoice }
}
